// Componentes muestra del tema "Clínico" (propuesta de diseño).
// Firma visual: "la columna clínica" — un trazo vertical izquierdo codifica el
// estado (color + icono + texto = triple señal, WCAG 1.4.1). Datos numéricos en
// IBM Plex Mono (panel de instrumentos); títulos de paciente en Fraunces.
import { ReactNode } from 'react';
import {
  Activity, CheckCheck, Clock, Droplet, HeartPulse, Stethoscope, Thermometer,
  AlertTriangle, Video, Wind, type LucideIcon,
} from 'lucide-react';

export type EstadoClinicoTipo = 'urgente' | 'estable' | 'pendiente' | 'completado';

const ESTADOS: Record<EstadoClinicoTipo, { label: string; Icon: LucideIcon; cls: string; border: string }> = {
  urgente:    { label: 'Urgente',    Icon: AlertTriangle, cls: 'bg-urgente-soft text-urgente',       border: 'border-urgente' },
  estable:    { label: 'Estable',    Icon: HeartPulse,    cls: 'bg-estable-soft text-estable',       border: 'border-estable' },
  pendiente:  { label: 'Pendiente',  Icon: Clock,         cls: 'bg-pendiente-soft text-pendiente',   border: 'border-pendiente' },
  completado: { label: 'Completado', Icon: CheckCheck,    cls: 'bg-completado-soft text-completado', border: 'border-completado' },
};

/** Indicador de estado clínico: color + icono + texto (nunca solo color). */
export function EstadoClinico({ estado, className = '' }: { estado: EstadoClinicoTipo; className?: string }) {
  const e = ESTADOS[estado];
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium ${e.cls} ${className}`}>
      <e.Icon className="h-3.5 w-3.5" aria-hidden /> {e.label}
    </span>
  );
}

/** Métrica clínica: etiqueta pequeña + valor en mono (panel de instrumentos). */
function Vital({ label, valor, unidad, Icon }: { label: string; valor: string; unidad?: string; Icon: LucideIcon }) {
  return (
    <div className="flex flex-col">
      <span className="flex items-center gap-1 text-[10px] uppercase tracking-wide text-muted-foreground">
        <Icon className="h-3 w-3" aria-hidden /> {label}
      </span>
      <span className="font-mono text-sm tabular-nums text-foreground">
        {valor}{unidad && <span className="text-muted-foreground text-xs"> {unidad}</span>}
      </span>
    </div>
  );
}

export interface PacienteClinico {
  nombre: string; edad: string; genero: string; sangre: string; identificacion: string;
  estado: EstadoClinicoTipo; alergias?: string; motivo?: string;
  vitales: { ta: string; fc: string; temp: string; spo2: string };
}

/** Vista CLÍNICA densa: ficha de paciente con columna de estado + vitales en mono. */
export function PacienteClinicoCard({ p }: { p: PacienteClinico }) {
  const e = ESTADOS[p.estado];
  return (
    <article className={`rounded-lg border border-l-4 ${e.border} bg-card text-card-foreground shadow-sm`}>
      <div className="flex items-start justify-between gap-3 p-4 pb-2">
        <div>
          <h3 className="text-base font-semibold leading-tight">{p.nombre}</h3>
          <p className="text-xs text-muted-foreground">
            {p.edad} · {p.genero} · <span className="font-mono">{p.identificacion}</span>
          </p>
        </div>
        <EstadoClinico estado={p.estado} />
      </div>

      {p.alergias && (
        <div className="mx-4 mb-2 flex items-center gap-1.5 rounded-md bg-urgente-soft px-2 py-1 text-xs text-urgente">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0" aria-hidden />
          <span className="font-medium">Alergias:</span> {p.alergias}
        </div>
      )}

      <div className="grid grid-cols-4 gap-2 border-t px-4 py-3">
        <Vital label="TA" valor={p.vitales.ta} unidad="mmHg" Icon={HeartPulse} />
        <Vital label="FC" valor={p.vitales.fc} unidad="lpm" Icon={Activity} />
        <Vital label="T°" valor={p.vitales.temp} unidad="°C" Icon={Thermometer} />
        <Vital label="SpO₂" valor={p.vitales.spo2} unidad="%" Icon={Wind} />
      </div>

      {p.motivo && (
        <div className="flex items-center gap-2 border-t px-4 py-2 text-sm">
          <Stethoscope className="h-4 w-4 text-primary shrink-0" aria-hidden />
          <span className="text-muted-foreground">Motivo:</span> {p.motivo}
          <Droplet className="ml-auto h-3.5 w-3.5 text-muted-foreground" aria-hidden />
          <span className="font-mono text-xs">{p.sangre}</span>
        </div>
      )}
    </article>
  );
}

export interface CitaPortal {
  saludo?: string; medico: string; especialidad: string; fecha: string; hora: string;
  tipo: 'presencial' | 'telematica';
}

/** Vista PACIENTE espaciosa: tarjeta de cita cálida, con título display y CTA. */
export function CitaPortalCard({ c, onAccion }: { c: CitaPortal; onAccion?: () => void }) {
  const esTele = c.tipo === 'telematica';
  return (
    <article className="overflow-hidden rounded-2xl border bg-card text-card-foreground shadow-sm">
      <div className="bg-primary/5 px-6 pt-6 pb-4">
        <p className="text-sm text-muted-foreground">{c.saludo ?? 'Tu próxima cita'}</p>
        <h3 className="mt-1 font-display text-2xl font-semibold leading-tight text-foreground">
          {c.fecha}
        </h3>
        <p className="font-display text-lg text-primary">{c.hora}</p>
      </div>
      <div className="space-y-3 px-6 py-5">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
            <Stethoscope className="h-5 w-5" aria-hidden />
          </span>
          <div>
            <p className="font-medium">{c.medico}</p>
            <p className="text-sm text-muted-foreground">{c.especialidad}</p>
          </div>
          <span className={`ml-auto inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-xs font-medium ${esTele ? 'bg-accent/15 text-accent' : 'bg-secondary text-secondary-foreground'}`}>
            {esTele ? <Video className="h-3.5 w-3.5" aria-hidden /> : <Stethoscope className="h-3.5 w-3.5" aria-hidden />}
            {esTele ? 'Telemática' : 'Presencial'}
          </span>
        </div>
        <button
          onClick={onAccion}
          className="flex w-full items-center justify-center gap-2 rounded-xl bg-accent px-4 py-3 text-base font-medium text-accent-foreground transition-transform hover:scale-[1.01] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-card"
        >
          {esTele ? <><Video className="h-5 w-5" aria-hidden /> Unirme a la videollamada</> : 'Ver detalle de mi cita'}
        </button>
      </div>
    </article>
  );
}

/** Bloque utilitario para el showcase. */
export function Panel({ titulo, children }: { titulo: string; children: ReactNode }) {
  return (
    <section className="space-y-3">
      <h2 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">{titulo}</h2>
      {children}
    </section>
  );
}
